import React from 'react';
import PropTypes from 'prop-types';

import {Box, Typography, Button, Stack, IconButton, Paper, Dropdown, DropdownMenuItem} from 'views/components';
import {Edit as EditIcon, FindReplaceOutlined as FindReplaceIcon} from 'views/icons';
import {Translate, translate} from 'views/module';

const ReplacementPreference = (props) => {
  const {replacementPreference, onSave} = props;
  const replacementPreferenceOptions = Object.entries(translate('client.ClientDetail.Careplan.ReplacementPreference.Options'));
  const [editing, setEditing] = React.useState(false);
  const [selectedReplacementPreference, setSelectedReplacementPreference] = React.useState(replacementPreference || 'NONE');
  const [mouseOver, setMouseOver] = React.useState(false);

  React.useEffect(() => {
    if (editing) return;
    setSelectedReplacementPreference(replacementPreference || 'NONE');
  }, [replacementPreference, editing]);

  const renderReplacementPreferenceLabel = (value) => {
    const option = replacementPreferenceOptions.find(([optionKey]) => optionKey === value);
    return option?.[1] || value;
  };

  const handleSave = async () => {
    const nextReplacementPreference = selectedReplacementPreference === 'NONE' ? null : selectedReplacementPreference;
    await onSave(nextReplacementPreference);
    setEditing(false);
  };

  const handleCancel = () => {
    setSelectedReplacementPreference(replacementPreference || 'NONE');
    setEditing(false);
  };

  return (
    <Paper
      sx={{
        borderRadius: 3,
        overflow: 'hidden',
      }}
      onMouseOver={() => setMouseOver(true)}
      onMouseLeave={() => setMouseOver(false)}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: 2,
          py: 1,
          backgroundColor: 'grey.100',
          justifyContent: 'space-between',
          borderRadius: '4px 4px 0 0',
          width: '100%',
        }}
      >
        <Box sx={{display: 'flex', alignItems: 'center'}}>
          <FindReplaceIcon sx={{mr: 1}} />
          <Typography variant="h6">
            <Translate expression="client.ClientDetail.Careplan.ReplacementPreference.Title" />
          </Typography>
        </Box>
        <IconButton
          sx={{
            visibility: editing ? 'hidden' : 'visible',
            opacity: mouseOver ? 1 : 0,
            transition: 'opacity 0.2s',
          }}
          onClick={() => setEditing(true)}
        >
          <EditIcon />
        </IconButton>
      </Box>
      <Box sx={{p: 2}}>
        {
          editing ? (
            <Stack spacing={2}>
              <Dropdown
                value={selectedReplacementPreference}
                onChange={setSelectedReplacementPreference}
                label={translate('client.ClientDetail.Careplan.ReplacementPreference.Field')}
              >
                {
                  replacementPreferenceOptions.map(([option, label]) => (
                    <DropdownMenuItem key={option} value={option}>
                      {label}
                    </DropdownMenuItem>
                  ))
                }
              </Dropdown>
              <Stack direction="row" justifyContent="flex-end" spacing={1} pt={1}>
                <Button onClick={handleCancel}>
                  <Translate expression="generic.Cancel" />
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSave}
                  sx={{minWidth: '100px'}}
                >
                  <Translate expression="generic.Save" />
                </Button>
              </Stack>
            </Stack>
          ) : (
            <Stack spacing={1}>
              <Typography variant="subtitle1" sx={{fontWeight: 'bold'}}>
                <Translate expression="client.ClientDetail.Careplan.ReplacementPreference.Field" />
              </Typography>
              <Typography variant="body2">
                {renderReplacementPreferenceLabel(selectedReplacementPreference)}
              </Typography>
            </Stack>
          )
        }
      </Box>
    </Paper>
  );
};

ReplacementPreference.propTypes = {
  replacementPreference: PropTypes.oneOf(['FIND_REPLACEMENT', 'SKIP_VISIT']),
  onSave: PropTypes.func.isRequired,
};

export default ReplacementPreference;
